import { Fragment, Suspense, lazy } from 'react';
import { OwlWait } from '@ruby/owl/react';
import { HqOwlWait } from './ui/HqOwlWait';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ModuleErrorBoundary } from './lib/ModuleErrorBoundary';
import { AuthProvider } from './shell/AuthProvider';
import { useAuth } from './shell/useAuth';
import { useAccess } from './shell/use-access';
import { LoginPage } from './shell/LoginPage';
import { ForgotPasswordPage } from './shell/ForgotPasswordPage';
import { AcceptInvitePage } from './shell/AcceptInvitePage';
import { Layout } from './shell/Layout';
import { isPathOpenToPages, resolveLandingPath } from './shell/nav-items';
import { Spinner } from './ui/Spinner';

const DashboardPage = lazy(() => import('./modules/dashboard'));
const ReleaseDetailsPage = lazy(() => import('./modules/dashboard/ReleaseDetailsPage'));
const DispatchPage = lazy(() => import('./modules/dispatch'));
const AIBookingPage = lazy(() => import('./modules/ai-booking'));
const KioskPage = lazy(() => import('./modules/kiosk'));
const SelfCheckinPage = lazy(() => import('./modules/self-checkin'));
const FrontDeskPage = lazy(() => import('./modules/front-desk'));
const OnlineBookingPage = lazy(() => import('./modules/online-booking'));
const CalendarPage = lazy(() => import('./modules/calendar'));
const ChatBotPage = lazy(() => import('./modules/chatbot'));
const DocumentsPage = lazy(() => import('./modules/document-manager'));
const AdminUsersPage = lazy(() => import('./modules/admin'));
const AuditLogPage = lazy(() => import('./modules/admin/AuditLogPage'));
const ProcedureSetupPage = lazy(() => import('./modules/admin/procedure-setup/ProcedureSetupPage'));
const ProcedureCheckupPage = lazy(() => import('./modules/admin/procedure-setup/checkup/ProcedureCheckupPage'));
const LocationsPage = lazy(() => import('./modules/admin/locations/LocationsPage'));
const ClinicConfigPage = lazy(() => import('./modules/admin/ClinicConfigPage'));
const SubrolesPage = lazy(() => import('./modules/admin/subroles/SubrolesPage'));
const WorkflowsListPage = lazy(() => import('./modules/admin/workflows/WorkflowsListPage'));
const WorkflowEditorPage = lazy(() => import('./modules/admin/workflows/WorkflowEditorPage'));
const WorkflowViewerPage = lazy(() => import('./modules/admin/workflows/WorkflowViewerPage'));
const DashboardTilesPage = lazy(() => import('./modules/admin/dashboard-tiles/DashboardTilesPage'));
const WorkflowDiagramAdminPage = lazy(() => import('./modules/admin/workflow-diagram/WorkflowDiagramAdminPage'));
const WorkflowMapPage = lazy(() => import('./modules/document-manager/WorkflowMapPage'));
const AddressBookPage = lazy(() =>
  import('./modules/address-book/AddressBookPage').then((m) => ({ default: m.AddressBookPage })),
);
const EmailTemplatesPage = lazy(() =>
  import('./modules/email-templates/EmailTemplatesPage').then((m) => ({
    default: m.EmailTemplatesPage,
  })),
);
const EmailedDocumentsPage = lazy(() =>
  import('./modules/emailed-documents/EmailedDocumentsPage').then((m) => ({
    default: m.EmailedDocumentsPage,
  })),
);
/** The one authed page that renders OUTSIDE `Layout` — see its route below. */
const ManualBookingWorkspace = lazy(() =>
  import('./modules/document-manager/manual-booking/ManualBookingWorkspace').then((m) => ({
    default: m.ManualBookingWorkspace,
  })),
);
const OcrEvalRoot = lazy(() => import('./modules/ocr-eval/OcrEvalRoot'));
const EncounterBenchmarkRoot = lazy(() => import('./modules/encounter-benchmark/EncounterBenchmarkRoot'));
const SettingsPage = lazy(() => import('./shell/SettingsPage'));

function ComingSoon() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Coming Soon</h2>
        <p className="text-sm text-gray-500">This module is under development.</p>
      </div>
    </div>
  );
}

function ModuleNotEnabled() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Module Not Available</h2>
        <p className="text-sm text-gray-500">This module is not enabled for your clinic. Contact support to upgrade.</p>
      </div>
    </div>
  );
}

function InactiveModuleBanner({ clinicName }: { clinicName: string }) {
  return (
    <div className="mx-4 mt-4 mb-0 border-2 border-amber-400 bg-amber-50 rounded-lg px-4 py-3 flex items-center gap-3">
      <div className="flex-shrink-0 w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center">
        <span className="text-white text-lg font-bold">!</span>
      </div>
      <div>
        <p className="text-sm font-semibold text-amber-800">Inactive Module</p>
        <p className="text-sm text-amber-700">
          This module is <span className="font-bold">not enabled</span> for <span className="font-bold">{clinicName}</span>. You are viewing it as an admin.
        </p>
      </div>
    </div>
  );
}

/** Remounts its children when the selected clinic changes, so a page that
 *  keeps rows, filters or an open document in state starts clean for the new
 *  clinic instead of polling the old one's until its next tick. The picker
 *  also sends Documents back to its list (`pathAfterClinicSwitch`).
 *  Plan: docs/plans/2026-10-22-owl-waits-everywhere.md (gap 2) */
function ClinicKeyed({ children }: { children: React.ReactNode }) {
  const { selectedClinic } = useAuth();
  return <Fragment key={selectedClinic ?? ''}>{children}</Fragment>;
}

/** Route guard that checks module eligibility. Admins and VELOX support
 *  bypass (they set clinics up) but see an inactive banner. */
function ProtectedModule({ moduleId, element }: { moduleId: string; element: React.ReactNode }) {
  const { clinics, selectedClinic } = useAuth();
  const { isStaff } = useAccess();
  const clinic = clinics.find(c => c.clientId === selectedClinic);
  const enabled = clinic?.enabledModules?.includes(moduleId);

  if (!enabled && isStaff) {
    return (
      <>
        <InactiveModuleBanner clinicName={clinic?.clinicName ?? selectedClinic ?? 'this clinic'} />
        {element}
      </>
    );
  }

  if (!enabled) return <ModuleNotEnabled />;
  return <>{element}</>;
}

/** `/` — sends the user to the clinic's configured landing page.
 *
 *  A component rather than an inline `<Navigate>` because the target depends on
 *  `useAuth()`, and `App()` is what RENDERS `<AuthProvider>` — a hook call in
 *  its body would sit outside the provider. This renders inside `Layout`'s
 *  `<Outlet>`, i.e. after `AuthGate` has settled `loading`, so `clinics` is
 *  populated by the time it runs.
 *
 *  Every "go home" in the app (the catch-all below, the `RubyOnly` / `StaffOnly` bounces, the
 *  error boundary's Back-to-home) points at `/`, so they all land correctly
 *  without knowing anything about the setting.
 *  Plan: docs/plans/2026-08-17-per-client-nav-and-landing.md */
function LandingRedirect() {
  const { user, clinics, selectedClinic } = useAuth();
  const clinic = clinics.find((c) => c.clientId === selectedClinic);
  return <Navigate to={resolveLandingPath(clinic ?? {}, clinic?.enabledModules ?? [], user?.pages)} replace />;
}

/** Ruby admins only — Ruby's own tooling and pipeline editors. */
function RubyOnly({ children }: { children: React.ReactNode }) {
  const { isRubyAdmin } = useAccess();
  if (!isRubyAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Ruby admins and VELOX support (from `min`, default L1). What each level may
 *  CHANGE on the page is decided per control with `canEdit`; the server refuses
 *  the rest. Plan: docs/plans/2026-09-25-ruby-hq-support-role.md §7. */
function StaffOnly({ children, min = 1 }: { children: React.ReactNode; min?: 1 | 2 }) {
  const { level } = useAccess();
  if (level < min) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** VELOX support L2 and up, or a clinic user manager — the server's
 *  `admin-api.ts` hook admits the same people. Plan:
 *  docs/plans/2026-10-22-clinic-user-managers.md. */
function UserAdminOnly({ children }: { children: React.ReactNode }) {
  const { level, isUserManager } = useAccess();
  if (level < 2 && !isUserManager) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Authentication only — no chrome.
 *
 * Renders an `<Outlet />` rather than `<Layout />` so a route can opt OUT of the
 * sidebar + header while still sitting behind the same login gate. Today that is
 * exactly one route (the manual-booking workspace, which needs the full
 * viewport); everything else is nested under the `<Layout />` layout route, so
 * for every other page this is the same two-step it always was.
 */
function AuthGate() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <OwlWait kind="open" size={96} label="Loading" fallback={<Spinner size="lg" />} />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // A VELOX support account without an authenticator reaches nothing but its
  // own account routes (server-enforced); send it to where enrolment happens.
  if (user.totpRequired && location.pathname !== '/settings') {
    return <Navigate to="/settings" replace />;
  }

  // A subrole confined to some pages (Front Desk): anything else goes home,
  // which lands on their page. The server refuses the other pages' API anyway
  // (`page-gate.ts`); this keeps a typed or bookmarked URL from rendering a
  // page of errors. Plan: docs/plans/2026-09-29-front-desk-subrole.md
  if (user.pages && !isPathOpenToPages(location.pathname, user.pages)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

/** Admin layout route: one shared error boundary for every admin sub-page.
 *  Per-nav remount is handled inside ModuleErrorBoundary (keys on pathname). */
function AdminBoundary() {
  return (
    <ModuleErrorBoundary module="Admin">
      <Outlet />
    </ModuleErrorBoundary>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes (no auth required) */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          <Route element={<AuthGate />}>
            {/* Chromeless — the ONE authed route outside `Layout`.
                The manual-booking workspace is three resizable columns
                (requisition · appointment cards · calendar) that need the whole
                viewport, and `Layout`'s `<main>` supplies padding plus its own
                `overflow-y-auto`, which would give the page a second scrollbar
                outside the columns.

                ⚠️ It carries its OWN `ModuleErrorBoundary` + `ProtectedModule`.
                Nested under `documents/*` it inherited both; hoisted, it
                inherits neither — and a render crash on the one page that
                writes to a real EMR must be a named fallback, not a white
                screen. `Layout`'s `<StagingBanner />` is re-rendered by the
                workspace itself for the same reason.
                Plan: docs/plans/2026-08-22-manual-book-fullscreen.md */}
            <Route
              path="documents/:id/book/manual"
              element={
                <ModuleErrorBoundary module="Documents">
                  {/* Its own Suspense: outside Layout, its lazy chunk had none
                      and the page sat blank while it loaded. */}
                  <Suspense
                    fallback={
                      <div className="min-h-screen flex items-center justify-center bg-gray-50">
                        <HqOwlWait kind="open" size={96} label="Loading" fallback={<Spinner size="lg" />} />
                      </div>
                    }
                  >
                    <ProtectedModule moduleId="document-manager" element={<ManualBookingWorkspace />} />
                  </Suspense>
                </ModuleErrorBoundary>
              }
            />

            <Route element={<Layout />}>
              <Route index element={<LandingRedirect />} />
              <Route path="dashboard" element={<ModuleErrorBoundary module="Dashboard"><DashboardPage /></ModuleErrorBoundary>} />
              <Route path="release-notes" element={<ModuleErrorBoundary module="Dashboard"><RubyOnly><ReleaseDetailsPage /></RubyOnly></ModuleErrorBoundary>} />
              <Route path="dispatch" element={<ModuleErrorBoundary module="Dispatch"><DispatchPage /></ModuleErrorBoundary>} />
              <Route path="ai-booking" element={<ModuleErrorBoundary module="Ruby AI Booking"><ProtectedModule moduleId="ai-booking" element={<AIBookingPage />} /></ModuleErrorBoundary>} />
              <Route path="waiting-room" element={<ModuleErrorBoundary module="Waiting Room"><ProtectedModule moduleId="waiting-room" element={<KioskPage />} /></ModuleErrorBoundary>} />
              <Route path="chatbot" element={<ModuleErrorBoundary module="Chat Bot"><ProtectedModule moduleId="chatbot" element={<ChatBotPage />} /></ModuleErrorBoundary>} />
              <Route path="front-desk" element={<ModuleErrorBoundary module="Front Desk"><ProtectedModule moduleId="self-checkin" element={<FrontDeskPage />} /></ModuleErrorBoundary>} />
              <Route path="self-checkin" element={<ModuleErrorBoundary module="Self Check-In"><ProtectedModule moduleId="self-checkin" element={<SelfCheckinPage />} /></ModuleErrorBoundary>} />
              {/* Not a paid module and not permission-gated — a read of the
                  clinic's own VELOX schedule, open to every role (2026-08-22).
                  The server still scopes it to the caller's clinics; clinics that
                  don't want it in the menu hide it via nav_hidden. */}
              <Route path="calendar" element={<ModuleErrorBoundary module="Calendar"><CalendarPage /></ModuleErrorBoundary>} />
              <Route path="online-booking" element={<ModuleErrorBoundary module="Online Booking"><ProtectedModule moduleId="online-booking" element={<OnlineBookingPage />} /></ModuleErrorBoundary>} />
              <Route path="documents/*" element={<ModuleErrorBoundary module="Documents"><ClinicKeyed><ProtectedModule moduleId="document-manager" element={<DocumentsPage />} /></ClinicKeyed></ModuleErrorBoundary>} />
              <Route path="workflow-map" element={<ModuleErrorBoundary module="Workflow Map"><WorkflowMapPage /></ModuleErrorBoundary>} />
              {/* Not a module — no ProtectedModule gate. Any clinic user who can
                  email a document can curate the book it sends to. */}
              <Route path="address-book" element={<ModuleErrorBoundary module="Email Address Book"><AddressBookPage /></ModuleErrorBoundary>} />
              {/* Same reasoning as the address book — the people who curate the
                  recipients also curate the wording. */}
              <Route path="email-templates" element={<ModuleErrorBoundary module="Email Templates"><EmailTemplatesPage /></ModuleErrorBoundary>} />
              {/* The register of what actually left the clinic. Same non-module,
                  non-admin reasoning as its two siblings above. */}
              <Route path="emailed-documents" element={<ModuleErrorBoundary module="Emailed Documents"><EmailedDocumentsPage /></ModuleErrorBoundary>} />
              {/* OCR Evals — staging-only, admin-only requisition-OCR tuning tool.
                  Its own ModuleErrorBoundary (not inside the AdminBoundary layout
                  route — it has its own tab strip + nested routes). The sidebar
                  entry is feature-flag-gated via GET /api/ocr-eval-config; the
                  route itself is RubyOnly + flag-enforced server-side. */}
              <Route path="ocr-eval/*" element={<ModuleErrorBoundary module="OCR Evals"><RubyOnly><OcrEvalRoot /></RubyOnly></ModuleErrorBoundary>} />
              {/* Encounter Benchmark — staging-only, admin-only; feature-flag-gated via
                  GET /api/encounter-benchmark-config (DEPLOY_ENV=staging + admin). */}
              <Route path="encounter-benchmark/*" element={<ModuleErrorBoundary module="Encounter Benchmark"><RubyOnly><EncounterBenchmarkRoot /></RubyOnly></ModuleErrorBoundary>} />
              <Route path="feedback" element={<ProtectedModule moduleId="feedback" element={<ComingSoon />} />} />
              <Route path="settings" element={<SettingsPage />} />
              {/* Admin: one ModuleErrorBoundary wraps all admin sub-pages via
                  layout route + <Outlet>, so any crash inside admin/* lands in
                  the same named fallback ("Something went wrong in Admin"). The
                  key={location.pathname} makes the boundary remount on admin
                  sub-nav so a crash on one sub-page doesn't stick when the user
                  navigates to a different admin page. */}
              <Route element={<AdminBoundary />}>
                <Route path="admin/users" element={<UserAdminOnly><AdminUsersPage /></UserAdminOnly>} />
                <Route path="admin/subroles" element={<RubyOnly><SubrolesPage /></RubyOnly>} />
                <Route path="admin/workflows" element={<RubyOnly><WorkflowsListPage /></RubyOnly>} />
                <Route path="admin/workflows/:clientId/:id/edit" element={<RubyOnly><WorkflowEditorPage /></RubyOnly>} />
                <Route path="admin/workflows/:clientId/:id/view" element={<RubyOnly><WorkflowViewerPage /></RubyOnly>} />
                <Route path="admin/dashboard-tiles" element={<RubyOnly><DashboardTilesPage /></RubyOnly>} />
                <Route path="admin/workflow-diagram" element={<RubyOnly><WorkflowDiagramAdminPage /></RubyOnly>} />
                {/* Procedures: Ruby admins and VELOX support. It was a bare route
                    until 2026-09-26, but the page's first read 403s a clinic user,
                    so nobody else could use it anyway. What each support level may
                    change is enforced by the /api/procedures PATCH handler. */}
                <Route path="admin/procedures" element={<StaffOnly><ProcedureSetupPage /></StaffOnly>} />
                {/* One procedure's checkup (plan docs/plans/2026-10-09-procedure-checkup.md). */}
                <Route path="admin/procedures/checkup/:code" element={<StaffOnly><ProcedureCheckupPage /></StaffOnly>} />
                <Route path="admin/locations" element={<StaffOnly><LocationsPage /></StaffOnly>} />
                <Route path="admin/velox" element={<Navigate to="/admin/procedures" replace />} />
                <Route path="admin/clinic-config" element={<StaffOnly><ClinicConfigPage /></StaffOnly>} />
                <Route path="admin/audit" element={<RubyOnly><AuditLogPage /></RubyOnly>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
